import React, { useMemo, useState } from 'react';
import { currentWeekStartIso } from './scheduler-ui.js';
import { useSchedulerReadModel } from './use-scheduler-read-model.js';
import type { SchedulerDriver } from './use-scheduler-read-model.js';
import SchedulerContextRail from './SchedulerContextRail.jsx';
import SchedulerAssignmentBoard from './SchedulerAssignmentBoard.jsx';
import SchedulerTimeOffPanel from './SchedulerTimeOffPanel.jsx';
import SchedulerWeeklyGrid from './SchedulerWeeklyGrid.jsx';
import SchedulerSearchBar from './SchedulerSearchBar.jsx';
import SchedulerDriverDetailDrawer from './SchedulerDriverDetailDrawer.jsx';
import SchedulerBulkAssign from './SchedulerBulkAssign.jsx';
import Drawer from '../../shared/ui/Drawer.js';
import { C } from '../../shared/design-tokens.js';

export default function SchedulerModule() {
  const [weekStart, setWeekStart] = useState(currentWeekStartIso());
  const [selectedTerminalId, setSelectedTerminalId] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedDriver, setSelectedDriver] = useState<SchedulerDriver | null>(null);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkAssignTerminalId, setBulkAssignTerminalId] = useState<string | null>(null);
  const [showTimeOff, setShowTimeOff] = useState(false);

  const {
    roster,
    assignmentGroups,
    terminalOptions,
    pendingRequests,
    decidedRequests,
    ptoEligible,
    gridData,
    loading,
    error,
    refreshScheduler,
  } = useSchedulerReadModel(weekStart, selectedTerminalId);

  const timeOffCount = useMemo(() => pendingRequests.length + decidedRequests.length, [pendingRequests, decidedRequests]);
  const pendingCount = pendingRequests.length;

  function openBulkAssign(terminalId?: string | null) {
    setBulkAssignTerminalId(terminalId || null);
    setShowBulkAssign(true);
  }

  function closeBulkAssign() {
    setShowBulkAssign(false);
    setBulkAssignTerminalId(null);
  }
  function closeTimeOff() { setShowTimeOff(false); }

  return (
    <div>
      <SchedulerContextRail
        weekStart={weekStart}
        selectedTerminalId={selectedTerminalId}
        terminalOptions={terminalOptions}
        onWeekStartChange={setWeekStart}
        onTerminalChange={setSelectedTerminalId}
      />

      <SchedulerSearchBar search={search} onSearchChange={setSearch} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => openBulkAssign(null)}
          disabled={loading}
        >
          + Bulk assign
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowTimeOff(true)}
        >
          Time off ({timeOffCount})
          {pendingCount > 0 ? (
            <span style={{ marginLeft: 6, color: C.gold, fontWeight: 700 }}>· {pendingCount} pending</span>
          ) : null}
        </button>
      </div>

      {loading ? <div style={{ color: C.textMuted, marginTop: 16 }}>Loading scheduler read model...</div> : null}
      {error ? <div style={{ color: C.red, marginTop: 16 }}>Scheduler load failed: {error}</div> : null}

      {!loading && !error ? (
        <>
          <SchedulerAssignmentBoard
            assignmentGroups={assignmentGroups}
            search={search}
            onOpenDriver={setSelectedDriver}
            onAssignTerminal={(terminalId) => openBulkAssign(terminalId)}
          />
          <SchedulerWeeklyGrid
            gridData={gridData}
            roster={roster}
            search={search}
            selectedTerminalId={selectedTerminalId}
            terminalOptions={terminalOptions}
            onOpenDriver={setSelectedDriver}
            onGridChange={refreshScheduler}
          />
        </>
      ) : null}

      <SchedulerDriverDetailDrawer
        driver={selectedDriver}
        onClose={() => setSelectedDriver(null)}
        terminalOptions={terminalOptions}
        onAssignmentChange={refreshScheduler}
      />

      <Drawer
        open={showBulkAssign}
        onClose={closeBulkAssign}
        title="Bulk assign"
        width={560}
      >
        <SchedulerBulkAssign
          roster={roster}
          terminalOptions={terminalOptions}
          initialTerminalId={bulkAssignTerminalId}
          onSuccess={() => { refreshScheduler(); closeBulkAssign(); }}
        />
      </Drawer>

      <Drawer
        open={showTimeOff}
        onClose={closeTimeOff}
        title="Time off"
        width={620}
      >
        <SchedulerTimeOffPanel
          pendingRequests={pendingRequests}
          decidedRequests={decidedRequests}
          ptoEligible={ptoEligible}
          onDecide={refreshScheduler}
        />
      </Drawer>
    </div>
  );
}
